import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';

export interface PageProgressSnapshot {
  routeRendered: boolean;
  suspenseCount: number;
  /** Number of currently mounted `usePageProgressReady` registrations. */
  registrationCount: number;
  allRegistrationsReady: boolean;
}

export interface PageProgressStore {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => PageProgressSnapshot;
  setRouteRendered: (isRendered: boolean) => void;
  beginSuspense: () => void;
  endSuspense: () => void;
  registerContent: (id: symbol, isReady: boolean) => void;
  unregisterContent: (id: symbol) => void;
}

function createPageProgressStore(): PageProgressStore {
  let routeRendered = false;
  let suspenseCount = 0;
  const registrations = new Map<symbol, boolean>();
  const listeners = new Set<() => void>();

  function compute(): PageProgressSnapshot {
    let allReady = true;
    for (const isReady of registrations.values()) {
      if (!isReady) {
        allReady = false;
        break;
      }
    }
    return {
      routeRendered,
      suspenseCount,
      registrationCount: registrations.size,
      allRegistrationsReady: allReady,
    };
  }

  let snapshot = compute();

  function emit(): void {
    const next = compute();
    if (
      next.routeRendered === snapshot.routeRendered &&
      next.suspenseCount === snapshot.suspenseCount &&
      next.registrationCount === snapshot.registrationCount &&
      next.allRegistrationsReady === snapshot.allRegistrationsReady
    ) {
      return;
    }
    snapshot = next;
    for (const listener of listeners) {
      listener();
    }
  }

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
    setRouteRendered(isRendered) {
      if (routeRendered === isRendered) return;
      routeRendered = isRendered;
      emit();
    },
    beginSuspense() {
      suspenseCount += 1;
      emit();
    },
    endSuspense() {
      suspenseCount = Math.max(0, suspenseCount - 1);
      emit();
    },
    registerContent(id, isReady) {
      if (registrations.get(id) === isReady && registrations.has(id)) {
        return;
      }
      registrations.set(id, isReady);
      emit();
    },
    unregisterContent(id) {
      if (!registrations.has(id)) return;
      registrations.delete(id);
      emit();
    },
  };
}

const PageProgressContext = createContext<PageProgressStore | null>(null);

interface PageProgressProviderProps {
  children: ReactNode;
  routeKey: string;
}

export function PageProgressProvider({
  children,
  routeKey,
}: PageProgressProviderProps) {
  const store = useMemo(() => createPageProgressStore(), [routeKey]);

  return (
    <PageProgressContext.Provider value={store}>
      {children}
    </PageProgressContext.Provider>
  );
}

// Context modules intentionally colocate these small hooks with their provider.
// eslint-disable-next-line react-refresh/only-export-components
export function usePageProgressStore(): PageProgressStore {
  const store = useContext(PageProgressContext);
  if (!store) {
    throw new Error('Page progress hooks require PageProgressProvider');
  }
  return store;
}

/**
 * Registers a critical readiness boundary for the route's primary content.
 *
 * Gate only work that must finish before the page's main content replaces its
 * initial spinner, skeleton, or hidden state. Secondary work must not be
 * registered: action mutations, overlays/modals, expanded details, lazy tabs,
 * exports/downloads, analytics, and background/refetch queries do not define
 * whether the route is ready.
 *
 * `isReady` means "settled", not necessarily successful. Error UI, an empty
 * result, an unavailable optional dependency, and redirect paths all count as
 * ready once no critical request or initialization can still produce the
 * primary content. A genuine pending state must remain false.
 *
 * Pages with their own primary loading state must use this hook; otherwise
 * progress falls back to global React Query activity.
 * Registration happens in a layout effect, which runs before the progress
 * reporter's own layout effect because the reporter is rendered after routes.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usePageProgressReady(isReady: boolean): void {
  const store = usePageProgressStore();
  const idRef = useRef<symbol | null>(null);
  idRef.current ??= Symbol('page-progress-content');

  useLayoutEffect(() => {
    store.registerContent(idRef.current as symbol, isReady);
  }, [store, isReady]);

  useLayoutEffect(() => {
    const id = idRef.current as symbol;
    return () => store.unregisterContent(id);
  }, [store]);
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePageProgressSnapshot(): PageProgressSnapshot {
  const store = usePageProgressStore();
  return useSyncExternalStore(store.subscribe, store.getSnapshot);
}

/** True when the lazy chunk mounted and every registration reports ready. */
// eslint-disable-next-line react-refresh/only-export-components
export function isSnapshotRouteReady(snapshot: PageProgressSnapshot): boolean {
  if (!snapshot.routeRendered || snapshot.suspenseCount > 0) {
    return false;
  }
  if (snapshot.registrationCount > 0) {
    return snapshot.allRegistrationsReady;
  }
  return true;
}

export function RouteSuspenseTracker(): null {
  const store = usePageProgressStore();

  useLayoutEffect(() => {
    store.setRouteRendered(true);
    return () => store.setRouteRendered(false);
  }, [store]);

  return null;
}

export function RouteSuspenseFallback({ children }: { children: ReactNode }) {
  const store = usePageProgressStore();

  useLayoutEffect(() => {
    store.beginSuspense();
    return () => store.endSuspense();
  }, [store]);

  return <>{children}</>;
}
